import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/translations';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

export const Footer = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';

  const scrollToSection = (sectionId) => {
    if (isLandingPage) {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const legalLinks = {
    pt: {
      privacy: 'Política de Privacidade',
      cookies: 'Política de Cookies',
    },
    en: {
      privacy: 'Privacy Policy',
      cookies: 'Cookie Policy',
    },
  };

  const legal = legalLinks[language] || legalLinks.pt;

  return (
    <footer data-testid="footer" className="bg-primary-deep text-white">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div>
              <h3 className="font-heading text-xl font-bold">Miguel Andrade</h3>
              <p className="text-sm text-mist-blue">Fisioterapia & Reabilitação</p>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Physical Therapy & Surf Injury Recovery
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="font-heading font-semibold text-lg">{t(translations.contact.title)}</h4>
            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-mist-blue flex-shrink-0" />
                <span>Edifício Atlântico, Rua Dr. Eduardo Burnay, Sala 12<br />2655-368 Ericeira, Portugal</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-mist-blue" />
                <a href="tel:+351911739498" className="hover:text-mist-blue transition-colors">+351 911 739 498</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-mist-blue" />
                <a href="mailto:info@diogoh.pt" className="hover:text-mist-blue transition-colors">info@diogoh.pt</a>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div className="space-y-4">
            <h4 className="font-heading font-semibold text-lg">{t(translations.contact.hours)}</h4>
            <div className="space-y-2 text-sm text-slate-300">
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-1 text-mist-blue flex-shrink-0" />
                <div className="space-y-1">
                  {t(translations.contact.hoursDetail).map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-heading font-semibold text-lg">Links</h4>
            <nav className="flex flex-col gap-2 text-sm text-slate-300">
              {isLandingPage ? (
                <>
                  <button onClick={() => scrollToSection('home')} className="text-left hover:text-mist-blue transition-colors">{t(translations.nav.home)}</button>
                  <button onClick={() => scrollToSection('services')} className="text-left hover:text-mist-blue transition-colors">{t(translations.nav.services)}</button>
                  <button onClick={() => scrollToSection('about')} className="text-left hover:text-mist-blue transition-colors">{t(translations.nav.about)}</button>
                  <button onClick={() => scrollToSection('appointments')} className="text-left hover:text-mist-blue transition-colors">{t(translations.nav.appointments)}</button>
                  <button onClick={() => scrollToSection('contact')} className="text-left hover:text-mist-blue transition-colors">{t(translations.nav.contact)}</button>
                </>
              ) : (
                <>
                  <Link to="/" className="hover:text-mist-blue transition-colors">{t(translations.nav.home)}</Link>
                </>
              )}
            </nav>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} Miguel Andrade. {t(translations.footer.rights)}.
            </p>
            
            {/* Legal Links */}
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link 
                to="/privacy" 
                className="hover:text-mist-blue transition-colors"
                data-testid="footer-privacy-link"
              >
                {legal.privacy}
              </Link>
              <Link 
                to="/cookies" 
                className="hover:text-mist-blue transition-colors"
                data-testid="footer-cookies-link"
              >
                {legal.cookies}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
